import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Fuel,
    Plus,
    Search,
    RefreshCw,
    Gauge,
    AlertTriangle,
    Truck,
    Calendar,
    Filter,
    MapPin,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DieselCargasPage() {
    const { hasPermission } = useDiesel();
    const [cargas, setCargas] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vehiculoFilter, setVehiculoFilter] = useState('');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        // Establecer rango de fechas por defecto (último mes)
        const hoy = new Date();
        const hace30dias = new Date();
        hace30dias.setDate(hace30dias.getDate() - 30);
        
        setFechaFin(hoy.toISOString().split('T')[0]);
        setFechaInicio(hace30dias.toISOString().split('T')[0]);
    }, []);

    useEffect(() => {
        if (fechaInicio && fechaFin) {
            loadData();
        }
    }, [vehiculoFilter, fechaInicio, fechaFin]);

    const loadData = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/diesel/cargas?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}&limit=200`;
            if (vehiculoFilter) {
                url += `&vehiculo_id=${vehiculoFilter}`;
            }

            const [cRes, vRes] = await Promise.all([
                fetch(url, { headers }),
                fetch(`${API_URL}/api/diesel/vehiculos?estado=todos`, { headers }),
            ]);

            if (cRes.ok) setCargas(await cRes.json());
            if (vRes.ok) setVehiculos(await vRes.json());
        } catch (err) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const getTipoCargaBadge = (tipo) => {
        switch (tipo) {
            case 'completa':
                return <Badge variant="default">Completa</Badge>;
            case 'parcial':
                return <Badge variant="secondary">Parcial</Badge>;
            case 'emergencia':
                return <Badge variant="outline">Emergencia</Badge>;
            default:
                return <Badge variant="secondary">{tipo}</Badge>;
        }
    };

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Historial de Cargas
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {cargas.length} registro{cargas.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {hasPermission('registrar_cargas') && (
                            <Link to="/diesel/cargas/nueva">
                                <Button className="bg-[var(--diesel-primary)] text-[var(--diesel-secondary)]">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Nueva Carga
                                </Button>
                            </Link>
                        )}
                        <Button variant="outline" onClick={loadData}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Filtros */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs uppercase text-muted-foreground">Vehículo</Label>
                                <select
                                    className="w-full p-2 border rounded-md bg-background"
                                    value={vehiculoFilter}
                                    onChange={(e) => setVehiculoFilter(e.target.value)}
                                >
                                    <option value="">Todos los vehículos</option>
                                    {vehiculos.map((v) => (
                                        <option key={v.id} value={v.id}>{v.patente}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase text-muted-foreground">Fecha Inicio</Label>
                                <Input
                                    type="date"
                                    value={fechaInicio}
                                    onChange={(e) => setFechaInicio(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase text-muted-foreground">Fecha Fin</Label>
                                <Input
                                    type="date"
                                    value={fechaFin}
                                    onChange={(e) => setFechaFin(e.target.value)}
                                />
                            </div>
                            <div className="flex items-end">
                                <Button variant="outline" onClick={loadData} className="w-full">
                                    <Filter className="h-4 w-4 mr-2" />
                                    Filtrar
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Tabla */}
                <Card className="border-2">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Fecha/Hora</TableHead>
                                <TableHead>Vehículo</TableHead>
                                <TableHead>Litros</TableHead>
                                <TableHead>Kilometraje</TableHead>
                                <TableHead>KM Recorridos</TableHead>
                                <TableHead>Rendimiento</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>GPS</TableHead>
                                <TableHead>Alerta</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                [...Array(8)].map((_, i) => (
                                    <TableRow key={i}>
                                        {[...Array(9)].map((_, j) => (
                                            <TableCell key={j}><Skeleton className="h-5 w-20" /></TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : cargas.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <div className="py-12 text-center">
                                            <Fuel className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                            <p className="font-medium">Sin registros</p>
                                            <p className="text-sm text-muted-foreground">
                                                No hay cargas en el período seleccionado
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                cargas.map((c) => (
                                    <TableRow key={c.id} className={c.alerta_generada ? 'bg-amber-500/5' : ''}>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium">
                                                    {new Date(c.fecha_hora).toLocaleDateString()}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {new Date(c.fecha_hora).toLocaleTimeString()}
                                                </p>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Truck className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-mono font-bold">
                                                    {c.vehiculo_info?.patente || '-'}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="font-mono">{c.litros_cargados} L</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="font-mono">{c.kilometraje_actual?.toLocaleString()}</span>
                                        </TableCell>
                                        <TableCell>
                                            {c.km_recorridos ? (
                                                <span className="font-mono">{c.km_recorridos?.toLocaleString()} km</span>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {c.rendimiento_calculado ? (
                                                <div className="flex items-center gap-1">
                                                    <Gauge className="h-4 w-4" style={{ color: 'var(--diesel-primary)' }} />
                                                    <span className="font-mono font-bold">
                                                        {c.rendimiento_calculado} km/l
                                                    </span>
                                                    {c.desviacion_porcentual && (
                                                        <span className={`text-xs ${
                                                            c.desviacion_porcentual > 10 ? 'text-destructive' : 'text-muted-foreground'
                                                        }`}>
                                                            ({c.desviacion_porcentual > 0 ? '+' : ''}{c.desviacion_porcentual}%)
                                                        </span>
                                                    )}
                                                </div>
                                            ) : c.es_carga_inicial ? (
                                                <Badge variant="outline">Inicial</Badge>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {getTipoCargaBadge(c.tipo_carga)}
                                        </TableCell>
                                        <TableCell>
                                            {c.latitud && c.longitud ? (
                                                <a
                                                    href={`https://www.google.com/maps?q=${c.latitud},${c.longitud}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    <MapPin className="h-4 w-4" />
                                                </a>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {c.alerta_generada && (
                                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Card>

                {/* Resumen */}
                {cargas.length > 0 && (
                    <Card className="border-2">
                        <CardContent className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                <div>
                                    <p className="text-2xl font-black" style={{ color: 'var(--diesel-primary)' }}>
                                        {cargas.reduce((sum, c) => sum + (c.litros_cargados || 0), 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground uppercase">Litros Totales</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-black">
                                        {cargas.reduce((sum, c) => sum + (c.km_recorridos || 0), 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground uppercase">KM Recorridos</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-black">
                                        {(() => {
                                            const cargasConRend = cargas.filter(c => c.rendimiento_calculado);
                                            if (cargasConRend.length === 0) return '0';
                                            const promedio = cargasConRend.reduce((sum, c) => sum + c.rendimiento_calculado, 0) / cargasConRend.length;
                                            return promedio.toFixed(2);
                                        })()}
                                    </p>
                                    <p className="text-xs text-muted-foreground uppercase">Rendimiento Prom.</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-black text-amber-600">
                                        {cargas.filter(c => c.alerta_generada).length}
                                    </p>
                                    <p className="text-xs text-muted-foreground uppercase">Alertas</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </DieselLayout>
    );
}
